import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { IoCodeSlashOutline, IoCreateOutline } from "react-icons/io5";
import { useNavigate } from "react-router";

import * as UI from "../../../../../modules/ui-kit";
import DefineBimClass from "../DefineBimClass.graphql";
import ReadBimClass from "./ReadBimClass.graphql";
import useNotifications, { notifyMutationErrors } from "../../../../../components/Notifications";
import CodeEditor from "../../../components/CodeEditor";
import { StyledEditorLayout } from "../../../components/StyledEditorLayout";
import {
    PropertiesField,
    RequirementGroupsField,
    RequirementsField,
    UrlListField,
} from "../../../components/fields";
import { sameSource } from "../../../modules/compare";
import { useUnsavedChangesEditor } from "../../../modules/unsaved-changes";

export default function BimClassEditor(props) {
    const { guard, id, revisionID } = props;
    const navigate = useNavigate();

    const notifications = useNotifications();

    const [mode, setMode] = useState<"form" | "json">("form");

    const readBimClass = useQuery(
        gql(ReadBimClass),
        {
            variables: {
                revisionID,
            },
        },
    );

    const storedSource = readBimClass.data?.bimClassRevision?.source ?? {};

    const [currentSource, setCurrentSource] = useState(undefined);
    const [jsonText, setJsonText] = useState("");
    const [jsonError, setJsonError] = useState(undefined);
    const [saving, setSaving] = useState(false);



    useEffect(
        () => {
            setCurrentSource(undefined);
            setJsonText(JSON.stringify(storedSource, null, 2));
            setJsonError(undefined);
        },
        [revisionID, readBimClass.data],
    );

    const activeSource = currentSource ?? storedSource;
    const dirty = !sameSource(activeSource, storedSource);

    const [defineBimClass] = useMutation(
        gql(DefineBimClass),
        {
            // Every save appends a revision: the version list has to be back
            // before we navigate to the new one.
            awaitRefetchQueries: true,
            refetchQueries: [
                "ListBimClassRevisions",
                "ListBimClasses",
                gql(ReadBimClass),
            ],
        },
    );

    function updateField(key: string, value: unknown) {
        setCurrentSource({
            ...activeSource,
            [key]: value,
        });
    }

    function onJsonChange(value: string | undefined) {
        const text = value ?? "";
        setJsonText(text);
        try {
            const parsed = JSON.parse(text);
            setCurrentSource(parsed);
            setJsonError(undefined);
        } catch (e) {
            setJsonError((e as Error).message);
        }
    }

    function switchMode(targetMode: "form" | "json") {
        setJsonError(undefined);
        if (targetMode === "json") {
            setJsonText(JSON.stringify(activeSource, null, 2));
        }
        setMode(targetMode);
    }

    async function save() {
        if (jsonError && mode === "json") return false;

        setSaving(true);

        try {
            const result = await defineBimClass(
                {
                    variables: {
                        id,
                        definition: activeSource,
                    },
                }
            );

            if (result.data?.defineBimClass?.errors?.length) {
                notifyMutationErrors(notifications, result.data.defineBimClass.errors);
                return false;
            }

            setCurrentSource(undefined);
            notifications.success("Saved.");

            const newRevisionID = result.data?.defineBimClass?.bimClass?.revisionID;
            if (newRevisionID && newRevisionID !== revisionID) {
                navigate(`../revisions/${ newRevisionID }`);
            }

            return true;
        } finally {
            setSaving(false);
        }
    }

    useUnsavedChangesEditor(guard, save, dirty);

    if (readBimClass.loading) return "loading";
    if (readBimClass.error) return "error";

    function onSubmit(event) {
        event.preventDefault();
        void save();
    }

    function onReset() {
        setCurrentSource(undefined);
        setJsonText(JSON.stringify(storedSource, null, 2));
        setJsonError(undefined);
    }

    const requirementGroups = activeSource.requirementGroups ?? [];
    const requirements = activeSource.requirements ?? activeSource.parameters ?? [];
    const properties = activeSource.properties ?? [];










    return (
        <StyledEditorLayout>
            <div className="mode-tabs">
                <button
                    className={ mode === "form" ? "active" : "" }
                    onClick={ () => switchMode("form") }
                    type="button"
                >
                    <IoCreateOutline /> Form View
                </button>
                <button
                    className={ mode === "json" ? "active" : "" }
                    onClick={ () => switchMode("json") }
                    type="button"
                >
                    <IoCodeSlashOutline /> JSON Source
                </button>
            </div>

            <UI.Form
                className="editor-body"
                onReset={ onReset }
                onSubmit={ onSubmit }
            >
                { mode === "form" ? (
                    <>
                        <UI.Field>
                            <UI.FieldLabel>name</UI.FieldLabel>
                            <UI.FieldDescription>Multi-language translations for the BIM Class name</UI.FieldDescription>
                            <UI.TranslatedInput
                                disabled={ saving }
                                label="name"
                                onChange={ (val) => updateField("name", val) }
                                value={ activeSource.name ?? [] }
                            />
                        </UI.Field>

                        <UI.Field>
                            <UI.FieldLabel>materialName</UI.FieldLabel>
                            <UI.FieldDescription>Multi-language translations for the material name (optional)</UI.FieldDescription>
                            <UI.TranslatedInput
                                disabled={ saving }
                                label="material name"
                                onChange={ (val) => updateField("materialName", val) }
                                value={ activeSource.materialName ?? [] }
                            />
                        </UI.Field>

                        <UI.Field>
                            <UI.FieldLabel>bimClassID</UI.FieldLabel>
                            <UI.FieldDescription>Unique identifier for this BIM Class</UI.FieldDescription>
                            <UI.TextInput
                                disabled={ saving }
                                onChange={ (val) => updateField("bimClassID", val) }
                                placeholder="e.g. wall_standard"
                                value={ activeSource.bimClassID ?? "" }
                            />
                        </UI.Field>

                        <UI.Field>
                            <UI.FieldLabel>type</UI.FieldLabel>
                            <UI.FieldDescription>BIM Class type designation</UI.FieldDescription>
                            <UI.TextInput
                                disabled={ saving }
                                onChange={ (val) => updateField("type", val) }
                                placeholder="e.g. ELEMENT / MATERIAL"
                                value={ activeSource.type ?? "" }
                            />
                        </UI.Field>

                        <UI.Field>
                            <UI.FieldLabel>deprecated</UI.FieldLabel>
                            <UI.FieldDescription>Toggle whether this BIM Class is active or deprecated</UI.FieldDescription>
                            <UI.SegmentedControl
                                disabled={ saving }
                                onChange={ (val) => updateField("deprecated", val) }
                                options={ [
                                    { label: "Deprecated", value: true },
                                    { label: "Not deprecated", value: false },
                                ] }
                                value={ Boolean(activeSource.deprecated) }
                            />
                        </UI.Field>

                        <UrlListField
                            addLabel="Add Preview Image URL"
                            description="Preview image URLs illustrating this BIM Class"
                            disabled={ saving }
                            label="previews"
                            onChange={ (val) => updateField("previews", val) }
                            value={ activeSource.previews }
                        />

                        <RequirementGroupsField
                            description="Logical groups organizing parameters for this BIM Class"
                            disabled={ saving }
                            label="requirementGroups"
                            onChange={ (val) => updateField("requirementGroups", val) }
                            value={ requirementGroups }
                        />

                        <RequirementsField
                            description="Parameters required or associated with this BIM Class"
                            disabled={ saving }
                            label="requirements"
                            onChange={ (val) => updateField("requirements", val) }
                            value={ requirements }
                        />

                        <PropertiesField
                            description="Assigned parameter properties and values (parameter.id &amp; value)"
                            disabled={ saving }
                            label="properties"
                            onChange={ (val) => updateField("properties", val) }
                            value={ properties }
                        />
                    </>
                ) : (
                    <UI.Field className="json-field">
                        <UI.FieldLabel>Raw JSON Definition</UI.FieldLabel>
                        <UI.FieldDescription>Edit full schema source including requirements, calculations, constraints, and geometry</UI.FieldDescription>
                        <div className="json-editor-wrapper">
                            <CodeEditor
                                defaultValue={ jsonText }
                                key={ revisionID + mode }
                                onChange={ onJsonChange }
                            />
                        </div>
                        { jsonError && <span className="errors">Invalid JSON: { jsonError }</span> }
                    </UI.Field>
                ) }


                <UI.ButtonGroup>
                    <UI.Button
                        disabled={ !dirty || saving }
                        type="reset"
                    >
                        Reset
                    </UI.Button>
                    <UI.Button
                        $appearance="primary"
                        disabled={ !dirty || (mode === "json" && !!jsonError) || saving }
                        type="submit"
                    >
                        { saving ? "Saving…" : "Save" }
                    </UI.Button>
                </UI.ButtonGroup>
            </UI.Form>
        </StyledEditorLayout>
    );
}
